import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { compressImage } from '../lib/compressImage'
import Toggle from '../components/Toggle'
import Toast from '../components/Toast'
import { useToast } from '../hooks/useToast'
import { useAuth } from '../hooks/useAuth'

const VEHICLE_TYPES = [
  { value: 'car',   label: 'Car',   icon: '🚗' },
  { value: 'bike',  label: 'Bike',  icon: '🏍️' },
  { value: 'truck', label: 'Truck', icon: '🚚' },
  { value: 'auto',  label: 'Auto',  icon: '🛺' },
]

const PET_ANIMALS = [
  { value: 'dog',    label: 'Dog',    icon: '🐕' },
  { value: 'cat',    label: 'Cat',    icon: '🐈' },
  { value: 'bird',   label: 'Bird',   icon: '🐦' },
  { value: 'rabbit', label: 'Rabbit', icon: '🐇' },
  { value: 'other',  label: 'Other',  icon: '🐾' },
]

const STEPS = ['Sign In', 'Your Details', 'Channels']

// Common countries with dial codes and flags
const COUNTRIES = [
  { code: '+91',  flag: '🇮🇳', name: 'India',          tz: ['Asia/Kolkata', 'Asia/Calcutta'] },
  { code: '+1',   flag: '🇺🇸', name: 'USA',             tz: ['America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Phoenix','America/Anchorage','Pacific/Honolulu'] },
  { code: '+44',  flag: '🇬🇧', name: 'UK',              tz: ['Europe/London'] },
  { code: '+61',  flag: '🇦🇺', name: 'Australia',       tz: ['Australia/Sydney','Australia/Melbourne','Australia/Brisbane','Australia/Perth','Australia/Adelaide'] },
  { code: '+971', flag: '🇦🇪', name: 'UAE',             tz: ['Asia/Dubai'] },
  { code: '+65',  flag: '🇸🇬', name: 'Singapore',       tz: ['Asia/Singapore'] },
  { code: '+60',  flag: '🇲🇾', name: 'Malaysia',        tz: ['Asia/Kuala_Lumpur'] },
  { code: '+49',  flag: '🇩🇪', name: 'Germany',         tz: ['Europe/Berlin'] },
  { code: '+33',  flag: '🇫🇷', name: 'France',          tz: ['Europe/Paris'] },
  { code: '+1',   flag: '🇨🇦', name: 'Canada',          tz: ['America/Toronto','America/Vancouver','America/Edmonton','America/Winnipeg','America/Halifax'] },
  { code: '+55',  flag: '🇧🇷', name: 'Brazil',          tz: ['America/Sao_Paulo'] },
  { code: '+81',  flag: '🇯🇵', name: 'Japan',           tz: ['Asia/Tokyo'] },
  { code: '+82',  flag: '🇰🇷', name: 'South Korea',     tz: ['Asia/Seoul'] },
  { code: '+86',  flag: '🇨🇳', name: 'China',           tz: ['Asia/Shanghai'] },
  { code: '+92',  flag: '🇵🇰', name: 'Pakistan',        tz: ['Asia/Karachi'] },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh',      tz: ['Asia/Dhaka'] },
  { code: '+94',  flag: '🇱🇰', name: 'Sri Lanka',       tz: ['Asia/Colombo'] },
  { code: '+977', flag: '🇳🇵', name: 'Nepal',           tz: ['Asia/Kathmandu'] },
  { code: '+27',  flag: '🇿🇦', name: 'South Africa',    tz: ['Africa/Johannesburg'] },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria',         tz: ['Africa/Lagos'] },
  { code: '+254', flag: '🇰🇪', name: 'Kenya',           tz: ['Africa/Nairobi'] },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia',    tz: ['Asia/Riyadh'] },
  { code: '+20',  flag: '🇪🇬', name: 'Egypt',           tz: ['Africa/Cairo'] },
  { code: '+34',  flag: '🇪🇸', name: 'Spain',           tz: ['Europe/Madrid'] },
  { code: '+39',  flag: '🇮🇹', name: 'Italy',           tz: ['Europe/Rome'] },
  { code: '+31',  flag: '🇳🇱', name: 'Netherlands',     tz: ['Europe/Amsterdam'] },
]


function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const match = COUNTRIES.find(c => c.tz.includes(tz))
    return match || COUNTRIES[0] // default to India
  } catch {
    return COUNTRIES[0]
  }
}

export default function Onboarding() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast, showToast, dismissToast } = useToast()
  const { user } = useAuth()

  const [step, setStep] = useState(0)   // 0 = category picker, 1-3 = onboarding steps
  const [tagCategory, setTagCategory] = useState(null)  // 'vehicle' | 'pet'
  const [loading, setLoading] = useState(false)

  // Step 1
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [countryCode, setCountryCode] = useState(() => detectCountry())
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState('')
  const [userId, setUserId] = useState(null)

  // Step 2
  const [vehicleType, setVehicleType] = useState('car')  // for vehicles; for pet this stays 'pet'
  const [plate, setPlate] = useState('')
  const [emergencyPhone, setEmergencyPhone] = useState('')
  const [emergencyCountryCode, setEmergencyCountryCode] = useState(() => detectCountry())

  // Pet/Child-specific
  const [petName, setPetName] = useState('')
  const [petAnimal, setPetAnimal] = useState('dog')
  const [petBreed, setPetBreed] = useState('')
  const [photoFile, setPhotoFile] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)

  // Step 3
  const [channelCall, setChannelCall] = useState(true)
  const [channelWhatsapp, setChannelWhatsapp] = useState(true)
  const [channelSms, setChannelSms] = useState(true)

  const progressPct = step < 1 ? 0 : ((step - 1) / (STEPS.length - 1)) * 100

  const isDemoMode = !import.meta.env.VITE_SUPABASE_URL || searchParams.get('demo') === '1'

  // Test number bypass — no SMS sent, fixed OTP accepted
  const TEST_PHONE = '9999999999'
  const TEST_OTP   = '000000'
  const isTestPhone = phone === TEST_PHONE && countryCode.code === '+91'

  // ── Step 1: send OTP ───────────────────────────────────────────────────────
  async function handleSendOtp(e) {
    e.preventDefault()
    if (phone.length < 10) { showToast('Enter a valid 10-digit number', 'error'); return }

    // Demo mode: skip OTP, go straight to next step
    if (isDemoMode) {
      showToast('Demo mode — skipping OTP verification')
      setStep(2)
      return
    }

    // Test number: skip SMS, show fixed OTP hint
    if (isTestPhone) {
      setOtpSent(true)
      showToast(`Test mode — use OTP ${TEST_OTP}`)
      return
    }

    setLoading(true)
    const { data, error } = await supabase.functions.invoke('send-otp', {
      body: { phone: `${countryCode.code}${phone}` },
    })
    setLoading(false)

    if (error || !data?.ok) {
      showToast(data?.error || 'Failed to send OTP', 'error')
    } else {
      setOtpSent(true)
      showToast(`OTP sent to ${countryCode.code} ${phone}`)
    }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault()
    if (otp.length < 4) { showToast('Enter the OTP you received', 'error'); return }

    // Test number: accept fixed OTP and proceed like demo mode
    if (isTestPhone) {
      if (otp !== TEST_OTP) {
        showToast(`Invalid OTP — use ${TEST_OTP} for test number`, 'error')
        return
      }
      showToast('Test mode — OTP accepted')
      setStep(2)
      return
    }

    setLoading(true)
    const { data, error } = await supabase.functions.invoke('verify-otp', {
      body: { phone: `${countryCode.code}${phone}`, code: otp },
    })
    setLoading(false)

    if (error || !data?.ok) {
      showToast(data?.error || 'Invalid OTP', 'error')
      return
    }

    // Set session from custom auth response
    await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    })

    setUserId(data.user_id)

    // Check for existing profile — pre-fill name if returning user, then continue
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id, name')
      .eq('id', data.user_id)
      .maybeSingle()

    if (existingProfile?.name) {
      setName(existingProfile.name)
    }

    // Always continue to step 2 — the user came here to add a new tag
    setStep(2)
  }

  // ── Step 2: vehicle details ────────────────────────────────────────────────
  function handleVehicleNext(e) {
    e.preventDefault()
    if (!name.trim()) { showToast('Enter your name', 'error'); return }
    if (tagCategory === 'pet' && !petName.trim()) { showToast("Enter your pet's name", 'error'); return }
    if (tagCategory === 'child' && !petName.trim()) { showToast("Enter your child's name", 'error'); return }
    if (tagCategory === 'vehicle' && !plate.trim()) { showToast('Enter your vehicle plate number', 'error'); return }
    setStep(3)
  }

  // ── Step 3: channels + final submit ───────────────────────────────────────
  async function handleFinalSubmit(e) {
    e.preventDefault()

    if (!channelCall && !channelWhatsapp && !channelSms) {
      showToast('Enable at least one contact channel', 'error')
      return
    }

    // Demo mode or test number: skip DB writes, go to tag ready page
    if (isDemoMode || isTestPhone) {
      const demoCode = 'demo-' + Math.random().toString(36).slice(2, 8)
      navigate(`/tag/${demoCode}?demo=1&name=${encodeURIComponent(name)}&plate=${encodeURIComponent(plate)}&type=${vehicleType}`)
      return
    }

    setLoading(true)

    try {
      const uid = userId || (await supabase.auth.getUser()).data?.user?.id
      if (!uid) throw new Error('Not authenticated')

      // 1. Upsert profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({ id: uid, name: name.trim(), phone: `+91${phone}` })

      if (profileError) throw profileError

      // 2. Upload photo if provided (compressed first)
      let photoUrl = null
      if (photoFile && (tagCategory === 'pet' || tagCategory === 'child')) {
        const compressed = await compressImage(photoFile)
        const path = `${uid}/${Date.now()}.jpg`
        const { error: uploadErr } = await supabase.storage
          .from('tag-photos')
          .upload(path, compressed, { upsert: true })
        if (!uploadErr) {
          const { data: { publicUrl } } = supabase.storage.from('tag-photos').getPublicUrl(path)
          photoUrl = publicUrl
        }
      }

      // 3. Insert vehicle / pet / child
      const isNonVehicle = tagCategory === 'pet' || tagCategory === 'child'
      const { data: vehicle, error: vehicleError } = await supabase
        .from('vehicles')
        .insert({
          user_id: uid,
          plate: isNonVehicle ? petName.trim() : plate.trim().toUpperCase(),
          type: vehicleType,
          emergency_phone: emergencyPhone.trim() ? `${emergencyCountryCode.code}${emergencyPhone.trim()}` : null,
          channel_call: channelCall,
          channel_whatsapp: channelWhatsapp,
          channel_sms: channelSms,
          ...(isNonVehicle && {
            pet_name: petName.trim(),
            pet_breed: petBreed.trim() || null,
            ...(tagCategory === 'pet' && { pet_animal: petAnimal }),
            ...(photoUrl && { photo_url: photoUrl }),
          }),
        })
        .select()
        .single()

      if (vehicleError) throw vehicleError

      // 3. Insert tag
      const tagCode = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      )
      const { error: tagError } = await supabase
        .from('tags')
        .insert({
          vehicle_id: vehicle.id,
          tag_code: tagCode,
        })

      if (tagError) throw tagError

      const displayName = isNonVehicle ? petName.trim() : plate.trim().toUpperCase()
      navigate(`/tag/${tagCode}?name=${encodeURIComponent(name.trim())}&plate=${encodeURIComponent(displayName)}&type=${vehicleType}`)
    } catch (err) {
      showToast(err.message || 'Something went wrong. Please try again.', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg text-brand-text font-sans">
      <Toast toast={toast} onDismiss={dismissToast} />

      {/* ── STEP 0: Category picker ───────────────────────────────────────────── */}
      {step === 0 && (
        <div className="relative min-h-screen flex flex-col items-center justify-center px-5 animate-fade-in">
          {/* Top nav — My Dashboard for returning users */}
          {user && (
            <div className="absolute top-4 right-5">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all hover:opacity-90"
                style={{ background: '#00e5a0', color: '#0a0a0f' }}
              >
                My Dashboard →
              </button>
            </div>
          )}

          <div className="w-full max-w-[480px] space-y-8">
            {/* Logo */}
            <div className="text-center">
              <span className="font-syne font-extrabold text-3xl">
                Park<span className="text-accent">Ping</span>
              </span>
              <p className="text-brand-muted text-sm mt-2">What would you like to register?</p>
            </div>

            {/* Three category cards */}
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => { setTagCategory('vehicle'); setVehicleType('car'); setStep(1) }}
                className="flex flex-col items-center justify-center gap-3 py-8 rounded-3xl transition-all hover:scale-105 active:scale-95"
                style={{ background: '#13131a', border: '2px solid rgba(255,255,255,0.08)' }}
              >
                <span className="text-5xl">🚗</span>
                <span className="font-syne font-bold text-base text-brand-text">Vehicle</span>
                <span className="text-xs text-brand-muted text-center px-1">Car, Bike, Truck…</span>
              </button>

              <button
                onClick={() => { setTagCategory('child'); setVehicleType('child'); setStep(1) }}
                className="flex flex-col items-center justify-center gap-3 py-8 rounded-3xl transition-all hover:scale-105 active:scale-95"
                style={{ background: '#13131a', border: '2px solid rgba(255,255,255,0.08)' }}
              >
                <span className="text-5xl">👶</span>
                <span className="font-syne font-bold text-base text-brand-text">Child</span>
                <span className="text-xs text-brand-muted text-center px-1">Safety ID tag</span>
              </button>

              <button
                onClick={() => { setTagCategory('pet'); setVehicleType('pet'); setStep(1) }}
                className="flex flex-col items-center justify-center gap-3 py-8 rounded-3xl transition-all hover:scale-105 active:scale-95"
                style={{ background: '#13131a', border: '2px solid rgba(255,255,255,0.08)' }}
              >
                <span className="text-5xl">🐾</span>
                <span className="font-syne font-bold text-base text-brand-text">Pet</span>
                <span className="text-xs text-brand-muted text-center px-1">Dog, Cat, Bird…</span>
              </button>
            </div>

            <p className="text-center text-xs text-brand-muted">
              Privacy-first QR tags — your number stays hidden
            </p>
          </div>
        </div>
      )}

      {step >= 1 && (
      <div className="mx-auto max-w-[480px] px-5 py-6">
        {/* Back button */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setStep(step - 1)}
            className="p-2 rounded-xl text-brand-muted hover:text-brand-text transition-colors"
            style={{ background: '#13131a' }}
          >
            <ChevronLeft size={18} />
          </button>
          <span className="font-syne font-extrabold text-xl">
            Park<span className="text-accent">Ping</span>
          </span>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((s, i) => (
              <span
                key={s}
                className="text-xs font-semibold transition-colors"
                style={{ color: i + 1 <= step ? '#00e5a0' : '#7a798a' }}
              >
                {s}
              </span>
            ))}
          </div>
          <div className="h-1.5 bg-surface2 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progressPct === 0 ? 33 : progressPct}%`, background: '#00e5a0', boxShadow: '0 0 8px rgba(0,229,160,0.5)' }}
            />
          </div>
          <p className="text-xs text-brand-muted mt-2">Step {step} of {STEPS.length}</p>
        </div>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h2 className="font-syne font-bold text-2xl mb-1">Sign in to ParkPing</h2>
            <p className="text-sm text-brand-muted mb-6">Enter your mobile number — we'll send a quick OTP to verify.</p>

            {!otpSent ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1.5">Mobile Number</label>
                  <div className="flex rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                    <select
                      value={countryCode.code + '|' + countryCode.name}
                      onChange={(e) => {
                        const [code, name] = e.target.value.split('|')
                        setCountryCode(COUNTRIES.find(c => c.code === code && c.name === name) || COUNTRIES[0])
                        setPhone('')
                      }}
                      className="px-2 py-3 text-sm font-semibold text-accent outline-none cursor-pointer"
                      style={{ background: '#1e1e28', border: 'none', borderRight: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code + c.name} value={c.code + '|' + c.name}>
                          {c.flag} {c.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                      placeholder="Phone number"
                      className="flex-1 px-3 py-3 bg-surface text-brand-text placeholder-brand-muted outline-none"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                  style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.35)' }}
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  Send OTP
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div
                  className="flex items-center gap-2 px-4 py-3 rounded-2xl text-sm"
                  style={{ background: 'rgba(0,229,160,0.08)', border: '1px solid rgba(0,229,160,0.2)', color: '#00e5a0' }}
                >
                  ✓ OTP sent to {countryCode.code} {phone}
                </div>

                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1.5">Enter OTP</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setOtp(val)
                      if (val.length === 6) {
                        setTimeout(() => document.getElementById('verify-otp-btn')?.click(), 100)
                      }
                    }}
                    placeholder="6-digit OTP"
                    className="w-full px-4 py-3 rounded-2xl text-brand-text text-center text-2xl tracking-[0.5em] placeholder-brand-muted outline-none transition-all"
                    style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    autoFocus
                    required
                  />
                </div>

                <button
                  id="verify-otp-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                  style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.35)' }}
                >
                  {loading && <Loader2 size={18} className="animate-spin" />}
                  Verify & Continue
                </button>

                <button
                  type="button"
                  onClick={() => { setOtpSent(false); setOtp('') }}
                  className="w-full text-sm text-brand-muted hover:text-brand-text transition-colors py-2"
                >
                  Change number
                </button>
              </form>
            )}
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div className="animate-fade-in">
            <h2 className="font-syne font-bold text-2xl mb-1">Your Details</h2>
            <p className="text-sm text-brand-muted mb-6">
              {tagCategory === 'pet'
                ? 'Almost there — tell us about yourself and your pet.'
                : tagCategory === 'child'
                ? 'Almost there — tell us about yourself and your child.'
                : 'Almost there — tell us about yourself and your vehicle.'}
            </p>

            <form onSubmit={handleVehicleNext} className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">
                  {tagCategory === 'pet' ? 'Owner Name' : tagCategory === 'child' ? 'Parent / Guardian Name' : 'Full Name'}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Rahul Sharma"
                  className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none transition-all"
                  style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                  required
                />
              </div>

              {/* Vehicle type — only shown for vehicle category */}
              {tagCategory === 'vehicle' && (
              <div>
                <label className="block text-sm font-medium text-brand-text mb-2">Vehicle Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {VEHICLE_TYPES.map((vt) => (
                    <button
                      key={vt.value}
                      type="button"
                      onClick={() => setVehicleType(vt.value)}
                      className="py-4 rounded-2xl flex flex-col items-center gap-1.5 transition-all hover:scale-105 active:scale-95"
                      style={{
                        background: vehicleType === vt.value ? 'rgba(0,229,160,0.12)' : '#13131a',
                        border: `2px solid ${vehicleType === vt.value ? '#00e5a0' : 'rgba(255,255,255,0.07)'}`,
                        boxShadow: vehicleType === vt.value ? '0 0 16px rgba(0,229,160,0.2)' : 'none',
                      }}
                    >
                      <span className="text-3xl">{vt.icon}</span>
                      <span
                        className="text-sm font-semibold"
                        style={{ color: vehicleType === vt.value ? '#00e5a0' : '#7a798a' }}
                      >
                        {vt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              )}

              {/* Pet fields */}
              {tagCategory === 'pet' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1.5">Pet Name</label>
                    <input
                      type="text"
                      value={petName}
                      onChange={(e) => setPetName(e.target.value)}
                      placeholder="e.g. Bruno"
                      className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none transition-all"
                      style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      required
                    />
                  </div>

                  {/* Photo upload */}
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Pet Photo <span className="text-brand-muted font-normal">(optional)</span>
                    </label>
                    <label className="flex flex-col items-center justify-center gap-3 py-5 rounded-2xl cursor-pointer transition-all hover:opacity-90"
                      style={{ background: '#13131a', border: `2px dashed ${photoPreview ? '#00e5a0' : 'rgba(255,255,255,0.15)'}` }}>
                      {photoPreview ? (
                        <img src={photoPreview} alt="preview" className="w-24 h-24 rounded-full object-cover" style={{ border: '2px solid #00e5a0' }} />
                      ) : (
                        <>
                          <span className="text-4xl">🐾</span>
                          <span className="text-sm text-brand-muted">Tap to upload a photo</span>
                        </>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        setPhotoFile(file)
                        setPhotoPreview(URL.createObjectURL(file))
                      }} />
                    </label>
                    {photoPreview && (
                      <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null) }}
                        className="mt-2 text-xs text-brand-muted hover:text-red-400 transition-colors w-full text-center">
                        Remove photo
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">Animal Type</label>
                    <div className="grid grid-cols-5 gap-2">
                      {PET_ANIMALS.map((a) => (
                        <button
                          key={a.value}
                          type="button"
                          onClick={() => setPetAnimal(a.value)}
                          className="py-3 rounded-xl flex flex-col items-center gap-1 transition-all"
                          style={{
                            background: petAnimal === a.value ? 'rgba(0,229,160,0.12)' : '#13131a',
                            border: `2px solid ${petAnimal === a.value ? '#00e5a0' : 'rgba(255,255,255,0.07)'}`,
                          }}
                        >
                          <span className="text-xl">{a.icon}</span>
                          <span className="text-xs" style={{ color: petAnimal === a.value ? '#00e5a0' : '#7a798a' }}>{a.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1.5">
                      Breed <span className="text-brand-muted font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={petBreed}
                      onChange={(e) => setPetBreed(e.target.value)}
                      placeholder="e.g. Labrador"
                      className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none transition-all"
                      style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    />
                  </div>
                </>
              )}

              {/* Child fields */}
              {tagCategory === 'child' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1.5">Child's Name</label>
                    <input
                      type="text"
                      value={petName}
                      onChange={(e) => setPetName(e.target.value)}
                      placeholder="e.g. Arjun"
                      className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none transition-all"
                      style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                      required
                    />
                  </div>

                  {/* Photo upload */}
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-2">
                      Child Photo <span className="text-brand-muted font-normal">(optional)</span>
                    </label>
                    <label className="flex flex-col items-center justify-center gap-3 py-5 rounded-2xl cursor-pointer transition-all hover:opacity-90"
                      style={{ background: '#13131a', border: `2px dashed ${photoPreview ? '#00e5a0' : 'rgba(255,255,255,0.15)'}` }}>
                      {photoPreview ? (
                        <img src={photoPreview} alt="preview" className="w-24 h-24 rounded-full object-cover" style={{ border: '2px solid #00e5a0' }} />
                      ) : (
                        <>
                          <span className="text-4xl">👶</span>
                          <span className="text-sm text-brand-muted">Tap to upload a photo</span>
                        </>
                      )}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        setPhotoFile(file)
                        setPhotoPreview(URL.createObjectURL(file))
                      }} />
                    </label>
                    {photoPreview && (
                      <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(null) }}
                        className="mt-2 text-xs text-brand-muted hover:text-red-400 transition-colors w-full text-center">
                        Remove photo
                      </button>
                    )}
                  </div>

                  <div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-brand-text mb-1.5">
                      Age / Description <span className="text-brand-muted font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={petBreed}
                      onChange={(e) => setPetBreed(e.target.value)}
                      placeholder="e.g. 5 years old male, wearing blue shirt"
                      className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none transition-all"
                      style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)' }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                      onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    />
                  </div>
                </>
              )}

              {/* Vehicle plate */}
              {tagCategory === 'vehicle' && (
                <div>
                  <label className="block text-sm font-medium text-brand-text mb-1.5">Number Plate</label>
                  <input
                    type="text"
                    value={plate}
                    onChange={(e) => setPlate(e.target.value.toUpperCase())}
                    placeholder="MH 02 AB 1234"
                    className="w-full px-4 py-3 rounded-2xl text-brand-text placeholder-brand-muted outline-none font-bold tracking-widest text-center transition-all"
                    style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.1)', fontSize: '1.1rem' }}
                    onFocus={(e) => (e.target.style.borderColor = 'rgba(0,229,160,0.5)')}
                    onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.1)')}
                    required
                  />
                </div>
              )}

              {/* Emergency contact */}
              <div>
                <label className="block text-sm font-medium text-brand-text mb-1.5">
                  Emergency Contact <span className="text-brand-muted font-normal">(optional)</span>
                </label>
                <div className="flex rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                  <select
                    value={emergencyCountryCode.code + '|' + emergencyCountryCode.name}
                    onChange={(e) => {
                      const [code, name] = e.target.value.split('|')
                      setEmergencyCountryCode(COUNTRIES.find(c => c.code === code && c.name === name) || COUNTRIES[0])
                      setEmergencyPhone('')
                    }}
                    className="px-2 py-3 text-sm font-semibold text-accent outline-none cursor-pointer"
                    style={{ background: '#1e1e28', border: 'none', borderRight: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code + c.name} value={c.code + '|' + c.name}>
                        {c.flag} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={emergencyPhone}
                    onChange={(e) => setEmergencyPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                    placeholder="Alternate number"
                    className="flex-1 px-3 py-3 bg-surface text-brand-text placeholder-brand-muted outline-none"
                  />
                </div>
                <p className="text-xs text-brand-muted mt-1.5">Used only if your primary number is unreachable</p>
              </div>

              <button
                type="submit"
                className="w-full py-4 rounded-2xl font-syne font-bold text-bg transition-all hover:opacity-90 active:scale-95"
                style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.35)' }}
              >
                Continue →
              </button>
            </form>
          </div>
        )}

        {/* ── STEP 3 ── */}
        {step === 3 && (
          <div className="animate-fade-in">
            <h2 className="font-syne font-bold text-2xl mb-1">Contact Channels</h2>
            <p className="text-sm text-brand-muted mb-6">
              Choose how people can reach you when they scan your QR tag.
            </p>

            <form onSubmit={handleFinalSubmit} className="space-y-4">
              {/* Channel toggles */}
              <div
                className="rounded-2xl p-4 space-y-4"
                style={{ background: '#13131a', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">📞</span>
                    <div>
                      <p className="text-sm font-semibold text-brand-text">Masked Call</p>
                      <p className="text-xs text-brand-muted">Voice call via proxy</p>
                    </div>
                  </div>
                  <Toggle checked={channelCall} onChange={setChannelCall} />
                </div>

                <div className="h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">💬</span>
                    <div>
                      <p className="text-sm font-semibold text-brand-text">WhatsApp</p>
                      <p className="text-xs text-brand-muted">Message via WhatsApp</p>
                    </div>
                  </div>
                  <Toggle checked={channelWhatsapp} onChange={setChannelWhatsapp} />
                </div>

                <div className="h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">✉️</span>
                    <div>
                      <p className="text-sm font-semibold text-brand-text">SMS</p>
                      <p className="text-xs text-brand-muted">Text message via proxy</p>
                    </div>
                  </div>
                  <Toggle checked={channelSms} onChange={setChannelSms} />
                </div>
              </div>

              {/* Summary card */}
              <div
                className="rounded-2xl p-4 space-y-2"
                style={{ background: 'rgba(0,229,160,0.06)', border: '1px solid rgba(0,229,160,0.18)' }}
              >
                <p className="text-xs font-semibold text-accent mb-2">Summary</p>
                <div className="flex items-center gap-2 text-xs text-brand-muted">
                  <span>{tagCategory === 'pet' ? '🐾' : tagCategory === 'child' ? '👶' : '🚗'}</span>
                  <span className="font-bold text-brand-text tracking-widest">
                    {tagCategory === 'vehicle' ? (plate || 'Your Plate') : (petName || (tagCategory === 'child' ? 'Your Child' : 'Your Pet'))}
                  </span>
                  <span className="text-brand-muted">·</span>
                  <span className="capitalize">{tagCategory === 'pet' ? petAnimal : vehicleType}</span>
                </div>
                <div className="flex gap-2 flex-wrap mt-1">
                  {channelCall && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,160,0.15)', color: '#00e5a0' }}>📞 Call</span>}
                  {channelWhatsapp && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,160,0.15)', color: '#00e5a0' }}>💬 WhatsApp</span>}
                  {channelSms && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,160,0.15)', color: '#00e5a0' }}>✉️ SMS</span>}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-2xl font-syne font-bold text-bg flex items-center justify-center gap-2 transition-all hover:opacity-90 active:scale-95 disabled:opacity-60"
                style={{ background: '#00e5a0', boxShadow: '0 0 20px rgba(0,229,160,0.35)' }}
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                Generate My Tag ✓
              </button>

              <p className="text-center text-xs text-brand-muted">
                🔒 Your number is always private. You can change these settings anytime.
              </p>
            </form>
          </div>
        )}
      </div>
      )}
    </div>
  )
}
